/**
 * Classic (set1) auth landing — visual/layout parity with docs/old MainScreen:
 * frost hero band, lang pill + sun/moon theme switch, client logo, menu tiles,
 * deco journey/consultation CTAs, login+biometric / register row.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Platform,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { resolveWebLayoutWidth } from '../shared/platform/webLayout';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ErrorState } from '../components/common/ErrorState';
import { Clickable2 } from '../components/common/Clickable2';
import { SvgIcon } from '../components/common/SvgIcon';
import { SunMoonIconSwitch } from '../components/common/SunMoonIconSwitch';
import { GradientMaskedImage } from '../components/ui/GradientMaskedImage';
import { ButtonLoader } from '../components/load-main/ButtonLoader';
import {
  fetchInitialConfig,
  getConfigLoadError,
  useBanners,
  useConsultationsFeatureEnabled, 
  useGuestFlowType,
  useGuestMenuListMode,
  useJourneyFeatureEnabled,
  useOnlineOrdersFeatureEnabled,
  useOrganizationSettings,
  useShowLogoClassicEnabled,
  useBannerLoopEnabled,
} from '../data/domains/config';
import {
  useAuthNavigation,
  useRedirectAuthenticatedFromGuestWelcome,
} from '../shared/navigation/useAuthNavigation';
import {
  MAIN_SCREEN_MENU_ROUTES,
  filterMainScreenMenuRoutes,
  resolveGuestRouteParams,
  shouldShowMainScreenConsultation,
  shouldShowMainScreenJourney,
  type MainScreenGuestRoute,
} from '../shared/navigation/guestMainScreenNav';
import {
  authenticateWithBiometrics,
  enrichBlockingSigninOtpParams,
  finalizeLoginSuccess,
  getAuthStore,
  hasStoredCredentials,
  isDeviceBiometryReady,
  loadStoredCredentials,
  persistSessionUser,
  setLoginJustHappened,
  signInWithPhonePassword,
  useAuthSheets,
} from '../shared/auth';
import { getFcmDeviceTokenForAuth } from '../platform/notifications/fcmDeviceToken';
import ClientLogoSvg, { HAS_CLIENT_LOGO } from '../shared/brand/clientLogo';
import { useTranslation, getLocale } from '../shared/i18n';
import {
  appTheme,
  persistThemeAppearance,
  useThemeColors,
  useThemeStore,
} from '../shared/theme';
import { androidScaleFont, androidScaleSize } from '../shared/theme/androidUiParity';
import { showErrorAlert } from '../components/appAlerts';
import { useAsyncWithLoading } from '../components/ui/hooks/useAsyncWithLoading';
import type { User } from '../data/domains/profile';
import AppIcon, { AppIconName } from '../components/common/icons/AppIcon';
import { MainScreenClassicBannerMedia } from './MainScreenClassicBannerMedia';
import { useReportSplashPageReady } from '../lib/useReportSplashPageReady';
import { DevHubGuestDevToolsEntry } from '../exDev';

/** Extra height below the status inset so the soft edge is visible into the hero. */
const FROST_FADE_EXTRA = 20;
/** SVG FeGaussianBlur stdDeviation — platforms differ slightly. */
const FROST_BLUR_STD = Platform.OS === 'ios' ? 10 : 18;

const CTA_H = androidScaleSize(52);
const CTA_RADIUS = androidScaleSize(16);
/** Under button height so journey/consult glyphs stay readable inside the clip. */
const CTA_DECO_ICON = Math.round(CTA_H * 1.2);

const MENU_TILE_ICONS: Record<MainScreenGuestRoute, AppIconName> = {
  Order: 'cart',
  DailyMenuFlow: 'receipt',
  SubscriptionList: 'calendar',
};

type MainCtaDecoShellProps = {
  side: 'start' | 'end';
  Icon: AppIconName;
  fill: string;
  icon_color: string;
  children: React.ReactNode;
};

/** One-off MainScreen CTA chrome — absolute rotated icon, overflow clipped by the shell. */
function MainCtaDecoShell({ side, Icon, fill, icon_color, children }: MainCtaDecoShellProps) {
  const is_end = side === 'end';
  return (
    <View style={[styles.ctaShell, { backgroundColor: fill, borderRadius: CTA_RADIUS }]}>
      <View style={[styles.ctaDecoBadge, is_end ? styles.ctaDecoEnd : styles.ctaDecoStart]} pointerEvents="none">
        {/* <Icon width={CTA_DECO_ICON} height={CTA_DECO_ICON} color={icon_color} /> */}
        <AppIcon name={Icon} size={CTA_DECO_ICON} color={icon_color} />
      </View>
      <View style={styles.ctaPressLayer}>{children}</View>
    </View>
  );
}

const MainScreenClassic = () => {
  useRedirectAuthenticatedFromGuestWelcome();
  const navigation = useAuthNavigation();
  const insets = useSafeAreaInsets();
  const appearance = useThemeStore(s => s.appearance);
  const is_dark_mode = appearance === 'dark';
  const { width: window_w, height: wh } = useWindowDimensions();
  const ww = resolveWebLayoutWidth(window_w);
  const MENU_TILE_SIZE = (ww - androidScaleSize(appTheme.spacing.lg) * 4) / 3;
  const colors = useThemeColors() ?? appTheme.colors;
  const { t, setLocale, getLocaleDisplayName } = useTranslation();
  const { openLogin, openRegister, openOtp } = useAuthSheets();
  const {
    settings: { dialing_code },
  } = useOrganizationSettings();
  const display_dialing_code = dialing_code != null ? String(dialing_code) : '965';
  const show_online_orders = useOnlineOrdersFeatureEnabled();
  const is_journey = useJourneyFeatureEnabled();
  const show_consultation = useConsultationsFeatureEnabled();
  const guest_menu_list = useGuestMenuListMode();
  const guest_flow_type = useGuestFlowType();
  const show_logo_classic = useShowLogoClassicEnabled();
  const banner_loop = useBannerLoopEnabled();
  const menu_tiles = useMemo(
    () => filterMainScreenMenuRoutes(MAIN_SCREEN_MENU_ROUTES, guest_menu_list, show_online_orders),
    [guest_menu_list, show_online_orders],
  );
  const show_journey_cta = shouldShowMainScreenJourney(guest_menu_list, is_journey);
  const show_consultation_cta = shouldShowMainScreenConsultation(guest_menu_list, show_consultation);
  const { heroMedia, backgroundImageUri, isLoading: banners_loading } = useBanners('banner');
  /** Still for frost mask only — video/YouTube use attachment poster when it is an image. */
  const frost_image_source = backgroundImageUri ? { uri: backgroundImageUri } : null;
  const hero_media_key =
    heroMedia == null
      ? null
      : `${heroMedia.kind}|${heroMedia.uri}|${heroMedia.fallback_uri ?? ''}`;
  /** Key match avoids reset-after-onLoad races that stranded splash forever. */
  const [hero_ready_key, set_hero_ready_key] = useState<string | null>(null);
  const hero_media_ready = hero_media_key == null || hero_ready_key === hero_media_key;
  const splash_page_ready = !banners_loading && hero_media_ready;
  useReportSplashPageReady(splash_page_ready);

  // Safety: if onLoad never fires, don't hold splash forever.
  useEffect(() => {
    if (banners_loading || hero_media_key == null || hero_ready_key === hero_media_key) {
      return;
    }
    const timer = setTimeout(() => set_hero_ready_key(hero_media_key), 2500);
    return () => clearTimeout(timer);
  }, [banners_loading, hero_media_key, hero_ready_key]);
  /** `show_logo_classic`: ClientLogoSvg only — never a non-SVG fallback; false hides even if SVG exists. */
  const show_client_logo = show_logo_classic && HAS_CLIENT_LOGO;

  const [app_config_error_message, setAppConfigErrorMessage] = useState<string | null>(getConfigLoadError);
  const [biometric_available, setBiometricAvailable] = useState(false);
  const [biometric_loading, run_biometric] = useAsyncWithLoading();
  const logo_size = Math.min(Math.max(ww, Math.round(ww)), ww);
  const next_locale = getLocale() === 'en' ? 'ar' : 'en';
  const lang_switch_label = getLocaleDisplayName(next_locale);
  const frost_h = Math.max(insets.top, 0) + FROST_FADE_EXTRA;
  const guest_nav = navigation as { navigate: (r: string, p?: Record<string, unknown>) => void };
  /** Prior classic palette on the frost layout — elevated tiles + primaryDark icons. */
  // const accent_text = colors.text;
  const accent_text = colors.primary;
  // const tile_fill = colors.surface;
  const tile_fill = colors.primaryText;
  const login_fill =  colors.primary;
  const login_label =  colors.primaryText;
  const register_fill =  colors.primaryText;
  const register_label = colors.primary;
  const biometric_fill =  colors.primaryText;
  const menu_icon_size = androidScaleSize(Math.min(MENU_TILE_SIZE / 1.25, appTheme.sizes.iconSizeLg + 10));

  useEffect(() => {
    setAppConfigErrorMessage(getConfigLoadError());
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const has_creds = await hasStoredCredentials();
        const hardware_ok = await isDeviceBiometryReady();
        if (!cancelled) {
          setBiometricAvailable(has_creds && hardware_ok);
        }
      } catch {
        if (!cancelled) {
          setBiometricAvailable(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRetryConfigLoad = async () => {
    setAppConfigErrorMessage(null);
    await fetchInitialConfig();
    setAppConfigErrorMessage(getConfigLoadError());
  };

  const completeLoginSuccess = useCallback(
    async (customer: User, loginPhone: string, loginPassword: string) => {
      await finalizeLoginSuccess({
        user: customer,
        onDismiss: () => undefined,
        session_options: { defer: 'none' },
        login_phone: loginPhone,
        login_password: loginPassword,
        offer_biometric_save: false,
        t,
      });
    },
    [t],
  );

  const handleBiometricLogin = useCallback(() => {
    void run_biometric(async () => {
      const authenticated = await authenticateWithBiometrics({
        title: t('auth.biometricPromptTitle'),
        subtitle: t('auth.biometricPromptSubtitle'),
      });
      if (!authenticated) {
        Alert.alert(t('auth.biometricUnavailable'), t('auth.biometricUnavailableShort'));
        return;
      }
      const credentials = await loadStoredCredentials();
      if (!credentials) {
        setBiometricAvailable(false);
        Alert.alert(t('auth.biometricUnavailable'), t('auth.biometricUnavailableMessage'));
        return;
      }

      const device_token = await getFcmDeviceTokenForAuth();
      const r = await signInWithPhonePassword(credentials.username, credentials.password, {
        device_token,
        skipStoreUpdate: true,
      });

      if (!r.ok) {
        showErrorAlert(t, { message: r.message?.trim() || t('auth.loginFailed') });
        return;
      }
      if (r.unverified) {
        await persistSessionUser(r.user);
        getAuthStore().setUser(r.user);
        setLoginJustHappened();
        openOtp(
          enrichBlockingSigninOtpParams({
            phone: credentials.username,
            countryCode: `+${display_dialing_code}`,
            redirectTo: 'MainTabs',
            loginData: { phone: credentials.username, password: credentials.password },
          }),
        );
        return;
      }
      await completeLoginSuccess(r.user, credentials.username, credentials.password);
    }).catch((error: unknown) => {
      showErrorAlert(t, {
        message: error instanceof Error ? error.message : t('auth.unexpectedError'),
      });
    });
  }, [completeLoginSuccess, display_dialing_code, openOtp, run_biometric, t]);

  if (app_config_error_message) {
    return (
      <View style={[styles.root, { backgroundColor: colors.background }]}>
        <ErrorState
          title={t('common.error')}
          message={app_config_error_message}
          onRetry={handleRetryConfigLoad}
          issue_context={{ page: 'Main Screen', issue: 'Initial configuration failed to load', kind: 'api', api_endpoint: 'initial', http_method: 'GET' }}
        />
      </View>
    );
  }

  const top_controls = (
    <View style={[styles.topRow, { paddingTop: Math.max(insets.top, appTheme.spacing.lg), zIndex: 2 }]}>
      <Clickable2
        onPress={() => setLocale(next_locale)}
        haptics
        style={[styles.langPill, { backgroundColor: colors.primaryText }]}
        accessibilityLabel={lang_switch_label}
        accessibilityRole="button">
        <Text style={[styles.langText, { color: colors.primary }]} numberOfLines={1}>
          {lang_switch_label}
        </Text>
      </Clickable2>
      <View style={styles.topTrailing}>
        <DevHubGuestDevToolsEntry />
        <SunMoonIconSwitch
          size={androidScaleSize(32)}
          value={is_dark_mode}
          onValueChange={dark => {
            void persistThemeAppearance(dark ? 'dark' : 'light');
          }}
          accessibilityLabel={t('settings.darkMode')}
        />
      </View>
    </View>
  );

  return (
    <View
      style={[
        styles.root,
        { backgroundColor: heroMedia ? '#000000' : colors.background },
      ]}>
      {heroMedia ? (
        <MainScreenClassicBannerMedia
          media={heroMedia}
          width={ww}
          height={wh}
          loop={banner_loop}
          onReady={() => {
            if (hero_media_key != null) {
              set_hero_ready_key(hero_media_key);
            }
          }}
        />
      ) : null}

      {frost_h > 0 ? (
        <View style={[styles.frostBand, { height: frost_h + androidScaleSize(40) }]}>
          {frost_image_source ? (
            <GradientMaskedImage
              source={frost_image_source}
              width={ww}
              height={frost_h + androidScaleSize(40)}
              imageHeight={wh}
              opaqueEdge="top"
              blurStdDeviation={FROST_BLUR_STD}
              tintColor={colors.background}
              tintOpacity={0.75}
              style={[{ position: 'absolute', top: 0, left: 0, zIndex: 1 }]}
            />
          ) : null}
          {top_controls}
        </View>
      ) : (
        top_controls
      )}

      <View
        style={[
          styles.content,
          {
            paddingTop: frost_h + androidScaleSize(40),
            paddingBottom: insets.bottom + appTheme.spacing.lg,
          },
        ]}>
        <View style={[styles.logoWrap, !show_client_logo ? styles.logoWrapHidden : null]} pointerEvents={show_client_logo ? 'auto' : 'none'}>
          <ClientLogoSvg width={0} height={logo_size} />
        </View>

        <View style={styles.actions}>
          <View style={styles.menuBtnGroup}>
            {menu_tiles.map(tile => {
              const Icon = MENU_TILE_ICONS[tile.route];
              return (
                <View key={tile.route} style={styles.menuBtnContainer}>
                  <Clickable2
                    onPress={() => {
                      const params = resolveGuestRouteParams(tile.route, guest_flow_type);
                      guest_nav.navigate(tile.route, params);
                    }}
                    haptics
                    accessibilityLabel={t(tile.labelKey)}
                    accessibilityRole="button"
                    style={[
                      styles.menuTile,
                      {
                        width: MENU_TILE_SIZE,
                        height: MENU_TILE_SIZE / 1.5,
                        backgroundColor: tile_fill,
                        borderRadius: androidScaleSize(16),
                      },
                    ]}>
                      <AppIcon name={Icon} size={menu_icon_size} color={accent_text} />
                    {/* <Icon width={menu_icon_size} height={menu_icon_size} color={accent_text} /> */}
                  </Clickable2>
                  <Text style={[styles.menuBtnLabel, { color: login_label }]} numberOfLines={1}>
                    {t(tile.labelKey)}
                  </Text>
                  <View
                    pointerEvents="none"
                    style={[
                      styles.menuBtnLabelOverlay,
                      {
                        backgroundColor: colors.primary,
                        borderRadius: androidScaleSize(16),
                      },
                    ]}
                  />
                </View>
              );
            })}
          </View>

          {show_journey_cta ? (
            <MainCtaDecoShell side="end" Icon="dash" fill={colors.primary} icon_color={colors.primaryText}>
              <Clickable2
                onPress={() => navigation.navigate('Journey', { from: 'main' })}
                haptics
                style={styles.ctaClickable}
                accessibilityLabel={t('main.getStarted')}
                accessibilityRole="button">
                <Text style={[styles.ctaLabel, { color: colors.primaryText }]} numberOfLines={1}>
                  {t('main.getStarted')}
                </Text>
              </Clickable2>
            </MainCtaDecoShell>
          ) : null}

          {show_consultation_cta ? (
            <MainCtaDecoShell side="start" Icon="consult" fill={colors.primary} icon_color={colors.primaryText}>
              <Clickable2
                onPress={() => navigation.navigate('Consultation')}
                haptics
                style={styles.ctaClickable}
                accessibilityLabel={t('main.talkToExpert')}
                accessibilityRole="button">
                <Text style={[styles.ctaLabel, { color: colors.primaryText }]} numberOfLines={1}>
                  {t('main.talkToExpert')}
                </Text>
              </Clickable2>
            </MainCtaDecoShell>
          ) : null}

          <View style={styles.btnGroup}>
            <View
              style={[
                styles.loginBtnGroup,
                {
                  height: androidScaleSize(52),
                  backgroundColor: login_fill,
                  borderRadius: androidScaleSize(16),
                },
              ]}>
              <Clickable2
                style={styles.loginBtn}
                onPress={() => openLogin({ presentation: 'default' })}
                haptics={true}
                hapticType="soft"
                accessibilityLabel={t('auth.login')}
                accessibilityRole="button">
                <Text style={[styles.loginBtnLabel, { color: login_label }]} numberOfLines={1}>
                  {t('auth.login')}
                </Text>
              </Clickable2>
              {biometric_available ? (
                <Clickable2
                  style={[
                    styles.iconBtn,
                    {
                      backgroundColor: biometric_fill,
                      borderRadius: androidScaleSize(10),
                    },
                  ]}
                  onPress={handleBiometricLogin}
                  disabled={biometric_loading}
                     haptics={true}
                hapticType="notificationError"
                  accessibilityLabel={t('auth.loginWithBiometrics')}
                  accessibilityRole="button">
                  {biometric_loading ? (
                    <ButtonLoader size={16} color={accent_text} />
                  ) : (
                    <SvgIcon name="fingerprint" size={androidScaleSize(32)} color={accent_text} />
                  )}
                </Clickable2>
              ) : null}
            </View>

            <Clickable2
              style={[
                styles.registerBtn,
                {
                  height: androidScaleSize(52),
                  backgroundColor: register_fill,
                  borderRadius: androidScaleSize(16),
                },
              ]}
              onPress={() => openRegister({ presentation: 'default' })}
              haptics={true}
              hapticType="soft"
              accessibilityLabel={t('auth.register')}
              accessibilityRole="button">
              <Text style={[styles.registerBtnLabel, { color: register_label }]} numberOfLines={1}>
                {t('auth.register')}
              </Text>
            </Clickable2>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  frostBand: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    paddingHorizontal: androidScaleSize(appTheme.spacing.lg),
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.lg,
    paddingBottom: appTheme.spacing.sm,
  },
  topTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.sm,
  },
  langPill: {
    minHeight: androidScaleSize(32),
    paddingHorizontal: appTheme.spacing.md,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  langText: {
    ...appTheme.typography.bodySmall,
    fontWeight: '600',
  },
  logoWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  /** Keep flex spacer; collapse width so the mark is gone without shifting the page. */
  logoWrapHidden: {
    width: 0,
    overflow: 'hidden',
  },
  actions: {
    gap: appTheme.spacing.sm,
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  menuBtnGroup: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
    width: '100%',
    marginBottom: 0,
  },
  menuBtnContainer: {
    flex: 1,
    alignItems: 'center',
    gap: appTheme.spacing.xs,
  },
  menuTile: {
    zIndex: 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
  },
  menuBtnLabel: {
    ...appTheme.typography.bodySmall,
    fontSize: androidScaleFont(14),
    fontWeight: '700',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: androidScaleSize(1),
    paddingBottom: androidScaleSize(4),
    lineHeight: androidScaleSize(16),
    zIndex: 3,
  },
  menuBtnLabelOverlay: {
    position: 'absolute',
    left: '6%',
    right: '6%',
    bottom: '0%',
    zIndex: 1,
    height: androidScaleSize(52),
  },
  loginBtnGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
    width: '40%',
    overflow: 'hidden',
  },
  btnGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  loginBtn: {
    flex: 1,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: appTheme.spacing.sm,
  },
  loginBtnLabel: {
    fontSize: androidScaleFont(16),
    fontWeight: '700',
    textAlign: 'center',
  },
  registerBtn: {
    flex: 2,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: appTheme.spacing.sm,
  },
  registerBtnLabel: {
    fontSize: androidScaleFont(16),
    fontWeight: '700',
    textAlign: 'center',
  },
  iconBtn: {
    width: androidScaleSize(40),
    height: androidScaleSize(40),
    marginHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaShell: {
    width: '100%',
    height: CTA_H,
    overflow: 'hidden',
  },
  ctaPressLayer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 4,
    justifyContent: 'center',
  },
  ctaClickable: {
    height: CTA_H,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: appTheme.spacing.md,
  },
  ctaLabel: {
    fontSize: androidScaleFont(16),
    fontWeight: '700',
    textAlign: 'center',
  },
  ctaDecoBadge: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: CTA_DECO_ICON,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    opacity: 1,
    
  },
  ctaDecoEnd: {
    right: '8%',
    transform: [{ rotate: '10deg' }, { translateY: Math.round(CTA_H * 0.16) }],
  },
  ctaDecoStart: {
    left: '8%',
    transform: [{ rotate: '-10deg' }, { translateY: Math.round(CTA_H * 0.16) }],
  },
});

export default MainScreenClassic;
